"use client";

import Image from "next/image";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface LeftTextRightImageProps {
	title: string;
	children: ReactNode;
	imageSrc: string;
	imageAlt?: string;
	/**
	 * false（默认）：标题在左栏内，与正文、图片同一 flex 行。
	 * true：标题独占整行，其下左文右图各占约 50%，文本与图片顶部对齐。
	 */
	titleFullWidth?: boolean;
	className?: string;
}

export default function LeftTextRightImage({
	title,
	children,
	imageSrc,
	imageAlt = "",
	titleFullWidth = false,
	className,
}: LeftTextRightImageProps) {
	if (titleFullWidth) {
		return (
			<section className={cn("w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16", className)}>
				<div className="container">
					<h2 className="mb-6 text-2xl font-bold leading-tight text-black sm:mb-8 sm:text-[30px] md:mb-10 md:text-4xl lg:mb-12">
						{title}
					</h2>

					<div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-8 xl:gap-10">
						<div className="w-full min-w-0 flex-1 lg:min-w-0">
							<div className="space-y-6 text-sm leading-relaxed text-black md:text-base">{children}</div>
						</div>
						<div className="w-full min-w-0 flex-1 self-start lg:min-w-0">
							<div className="relative aspect-[4/3] w-full overflow-hidden shadow-lg">
								<Image
									src={imageSrc}
									alt={imageAlt || title}
									fill
									className="object-cover"
									sizes="(max-width: 1024px) 100vw, 50vw"
								/>
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className={cn("w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				<div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-12 xl:gap-16">
					<div className="w-full lg:w-1/2">
						<div className="text-5xl font-bold leading-none text-themePrimary400 lg:text-[100px]">*</div>

						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{title}
						</h2>
						<div className="space-y-6 text-sm leading-relaxed text-black md:text-base">{children}</div>
					</div>
					<div className="w-full lg:sticky lg:top-24 lg:w-1/2">
						<div className="relative aspect-[4/3] w-full overflow-hidden">
							<Image
								src={imageSrc}
								alt={imageAlt || title}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
